import React from 'react';
import { Link } from 'react-router-dom';
import CaseStudyGate from '../../components/CaseStudyGate';
import Footer from '../../components/Footer';
import OtherProjects from '../../components/OtherProjects';
import useNoIndex from '../../hooks/useNoIndex';

// Flok brand blue, sampled from the app's splash screen and Sign Up button.
// It's too light for small text on the page background, so text uses a
// darker shade of the same blue that clears WCAG AA contrast.
const ACCENT = '#00A0E0';
const ACCENT_TEXT = '#0077A8';

// Flow chart palette shared with the Herba and WhoSay case studies: light gray
// panels with solid, borderless step boxes, echoing the Culture Amp journey row.
const WIRE = {
  card: '#ECEEF1',
  node: '#56697C',
  ink: '#2B3A4A',
  onNode: '#FFFFFF',
  onNodeMuted: 'rgba(255, 255, 255, 0.8)',
  line: '#7A8591',
  muted: '#8A94A0',
  accent: '#4F7FA0',
};

// Deliberately shorter than the Herba/Disco/Culture Amp case studies: an
// older, smaller project presented as a visual-design showcase, with no
// process narrative or Outcome section.

const SectionHeading = ({ eyebrow, title, intro }) => (
  <div className="mb-12">
    <p className="text-sm font-bold uppercase tracking-widest mb-3" style={{ color: ACCENT_TEXT }}>
      {eyebrow}
    </p>
    <h2 className="text-3xl sm:text-4xl font-bold text-text-primary mb-4">{title}</h2>
    {intro && <p className="text-tertiary text-lg max-w-2xl">{intro}</p>}
  </div>
);

// A single flat phone screenshot (status bar included, no bezel), shown at
// roughly phone size with a real shadow. Square corners so nothing at the
// screen's edges (status bar, tab bar) gets clipped.
const PhoneScreen = ({ src, alt }) => (
  <img
    src={src}
    alt={alt}
    className="w-full max-w-[260px] h-auto mx-auto shadow-xl border border-gray-light"
  />
);

// A phone that already has its own device bezel, cut out of the original
// group shot onto a transparent background so it sits directly on the page.
const DevicePhone = ({ src, alt }) => (
  <img src={src} alt={alt} className="w-full max-w-[240px] h-auto mx-auto" />
);

const ONBOARDING = [
  ['onboarding-welcome', 'Flok welcome screen with Log In and Sign Up, and a preview of a business club app'],
  ['onboarding-business-info', 'Your Business Info step: choosing a category, with Dentist selected under Health'],
  ['onboarding-cover-image', 'Select Cover Image step: a grid of photos to choose from, or upload your own'],
  ['onboarding-business-color', 'Select Business Color step: a palette of brand colors to choose from'],
  ['onboarding-preview', "Preview Your App step: the merchant's club with its punch card, and a Launch App button"],
];

const ANALYTICS = [
  {
    file: 'analytics-overview',
    alt: 'Analytics overview: 325 total club members split into Flok and in-store sign-ups, with club health trends for new members, app visits, and check-ins',
    label: 'Analytics',
    caption: 'A dashboard overview: total club members, sign-up sources, and a preview of club health, ratings, and punch card activity',
  },
  {
    file: 'analytics-ratings',
    alt: 'Ratings screen: a 4.5 average club rating with a breakdown by star count and recent customer ratings',
    label: 'Customer ratings',
    caption: 'How customers rate their experience with the business',
  },
  {
    file: 'analytics-club-health',
    alt: 'Club Health screen: a weekly chart of new members with a member breakdown below',
    label: 'Club health',
    caption: 'Check-ins, new members, and app visits over time',
  },
];

// A step in a flow chart. `end` marks a flow's final step with a darker box.
const FlowNode = ({ label, sub, end }) => (
  <div
    className="rounded-lg px-4 py-3 text-center text-sm font-medium"
    style={{ backgroundColor: end ? WIRE.ink : WIRE.node, color: WIRE.onNode }}
  >
    {label}
    {sub && (
      <span className="block text-xs font-normal mt-1" style={{ color: WIRE.onNodeMuted }}>
        {sub}
      </span>
    )}
  </div>
);

const FlowLabel = ({ children }) => (
  <p className="text-[10px] font-bold uppercase tracking-widest text-center mb-3" style={{ color: WIRE.accent }}>
    {children}
  </p>
);

const Arrow = ({ className = '' }) => (
  <div className={`text-center leading-none py-2 ${className}`} style={{ color: WIRE.muted }} aria-hidden="true">
    ↓
  </div>
);

// Splash screen branching into returning-merchant sign-in and new-merchant
// sign-up, both converging on the merchant dashboard. Built in code rather than
// as an image so the type stays crisp and it stacks cleanly on mobile.
const SignInFlow = () => (
  <div
    className="rounded-lg p-6 sm:p-8 max-w-3xl mx-auto"
    style={{ backgroundColor: WIRE.card }}
    role="img"
    aria-label="Flow chart: the splash screen branches into sign-in for existing merchants and sign-up for new merchants, and both paths end on the merchant dashboard"
  >
    <FlowLabel>Sign-in and account creation</FlowLabel>
    <div className="max-w-xs mx-auto">
      <FlowNode label="Splash screen" />
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-10 mt-2">
      <div className="flex flex-col">
        <Arrow />
        <FlowLabel>Sign in · existing merchant</FlowLabel>
        <FlowNode label="Email or Facebook sign-in" />
        {/* The sign-in branch is shorter, so a dashed line carries it down
            to the dashboard alongside the longer sign-up branch. */}
        <div className="hidden sm:flex flex-1 flex-col items-center pt-2" aria-hidden="true">
          <div className="flex-1 border-l border-dashed" style={{ borderColor: WIRE.muted }} />
          <Arrow />
        </div>
      </div>
      <div className="flex flex-col">
        <Arrow />
        <FlowLabel>Sign up · new merchant</FlowLabel>
        <FlowNode label="Business info" />
        <Arrow />
        <FlowNode label="Cover image and brand color" />
        <Arrow />
        <FlowNode label="Preview and launch" sub="Live preview of their club" />
        <Arrow className="hidden sm:block mt-auto" />
      </div>
    </div>
    {/* Each branch gets its own arrow into the dashboard on wide screens;
        once the branches stack on mobile, one shared arrow is clearer. */}
    <Arrow className="sm:hidden" />
    <div className="max-w-xs mx-auto">
      <FlowNode label="Merchant dashboard" end />
    </div>
  </div>
);

// Boxes for the campaign creation flow chart, positioned on a fixed SVG grid.
// x is the box's center; y is its top edge. Boxes with a subtitle are taller.
const CAMPAIGN_NODES = {
  create: { x: 300, y: 36, w: 170, label: 'Create campaign' },
  message: { x: 120, y: 126, w: 170, label: 'Message' },
  reward: { x: 480, y: 126, w: 170, label: 'Reward' },
  type: { x: 120, y: 216, w: 190, label: 'Type message', sub: 'shows reach before sending' },
  visit: { x: 320, y: 216, w: 140, label: 'Visit' },
  call: { x: 480, y: 216, w: 140, label: 'Call now' },
  buy: { x: 640, y: 216, w: 140, label: 'Buy now' },
  send: { x: 120, y: 326, w: 170, label: 'Send message', end: true },
  price: { x: 640, y: 306, w: 170, label: 'Set price', sub: 'original and new price' },
  expire: { x: 480, y: 426, w: 190, label: 'Set expiration', sub: 'day, week, month, or never' },
  launch: { x: 480, y: 526, w: 190, label: 'Review and send', sub: 'Send to 42 members', end: true },
};

const CAMPAIGN_EDGES = [
  ['create', 'message'],
  ['create', 'reward'],
  ['message', 'type'],
  ['type', 'send'],
  ['reward', 'visit'],
  ['reward', 'call'],
  ['reward', 'buy'],
  ['buy', 'price'],
  ['visit', 'expire'],
  ['call', 'expire'],
  ['price', 'expire'],
  ['expire', 'launch'],
];

const nodeHeight = (n) => (n.sub ? 54 : 40);

// Orthogonal connector: straight down, one horizontal run at a right angle,
// then straight down into the next box. Aligned boxes get a straight line.
// Converging edges share their horizontal run just above the target box.
const edgePath = (from, to) => {
  const x1 = from.x;
  const y1 = from.y + nodeHeight(from);
  const x2 = to.x;
  const y2 = to.y - 2;
  if (x1 === x2) return `M ${x1} ${y1} V ${y2}`;
  const turnY = to.y - 22;
  return `M ${x1} ${y1} V ${turnY} H ${x2} V ${y2}`;
};

// Campaign creation: a message path that ends in "Send message", and a reward
// path whose three reward types converge on a shared expiration step before
// "Launch campaign". Drawn as SVG so every connector is an exact right angle.
// On narrow screens it keeps its size and scrolls sideways rather than
// shrinking the type.
const CampaignFlow = () => (
  <div
    className="rounded-lg p-4 sm:p-6 overflow-x-auto"
    style={{ backgroundColor: WIRE.card }}
  >
    <svg
      viewBox="0 0 760 590"
      className="w-full min-w-[620px] h-auto"
      role="img"
      aria-labelledby="campaign-flow-title"
    >
      <title id="campaign-flow-title">
        Flow chart: Create campaign branches into Message and Reward. Message leads to Type
        message, which shows reach before sending, then Send message. Reward branches into Visit,
        Call now, and Buy now; Buy now first requires Set price. Visit, Call now, and Set price
        converge on Set expiration, then Review and send, sending to 42 members.
      </title>
      <defs>
        <marker id="campaign-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto">
          <path d="M 0 0 L 10 5 L 0 10 z" fill={WIRE.line} />
        </marker>
      </defs>
      <text x="380" y="14" textAnchor="middle" fontSize="10" fontWeight="700" letterSpacing="1.5" fill={WIRE.accent}>
        CAMPAIGN CREATION FLOW
      </text>
      {CAMPAIGN_EDGES.map(([a, b]) => (
        <path
          key={`${a}-${b}`}
          d={edgePath(CAMPAIGN_NODES[a], CAMPAIGN_NODES[b])}
          fill="none"
          stroke={WIRE.line}
          strokeWidth="1.25"
          markerEnd="url(#campaign-arrow)"
        />
      ))}
      {Object.entries(CAMPAIGN_NODES).map(([key, n]) => {
        const h = nodeHeight(n);
        return (
          <g key={key}>
            <rect
              x={n.x - n.w / 2}
              y={n.y}
              width={n.w}
              height={h}
              rx="8"
              fill={n.end ? WIRE.ink : WIRE.node}
            />
            <text
              x={n.x}
              y={n.y + (n.sub ? 22 : 25)}
              textAnchor="middle"
              fontSize="13"
              fontWeight="500"
              fill={WIRE.onNode}
            >
              {n.label}
            </text>
            {n.sub && (
              <text x={n.x} y={n.y + 40} textAnchor="middle" fontSize="11" fill={WIRE.onNodeMuted}>
                {n.sub}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  </div>
);

// In flow order, one screen per step of the campaign flow chart above.
const CAMPAIGN_SCREENS = [
  {
    file: 'campaign-type.jpg',
    alt: 'Create Campaign screen asking the merchant to choose a campaign type: Message or Reward',
    caption: 'Message or reward: the first fork in every campaign',
  },
  {
    file: 'reward-type.jpg',
    alt: 'Create Campaign screen asking the merchant to choose a reward type: Visit, Call Now, or Buy Now',
    caption: 'Visit, call-now, or buy-now, each a different incentive to bring customers back',
  },
  {
    file: 'type-message.jpg',
    alt: 'Type Message screen with a drafted message, a note that it will reach 42 members, and a Next button above the keyboard',
    caption: 'Composing a message, with reach shown before sending',
  },
  {
    file: 'set-price.jpg',
    alt: 'Set price screen with an original price of $120.00 and a new price of $60.00',
    caption: 'Original and discounted price for a buy-now reward',
  },
  {
    file: 'set-expiration.jpg',
    alt: 'Reward Will Expire In screen with options for 1 Day, 1 Week, 1 Month, or Never, with Never selected',
    caption: 'How long the reward stays valid: a day, a week, a month, or never',
  },
  {
    file: 'review-and-send.jpg',
    alt: 'Review and Send Campaign screen showing the message, a $60.00 price with $120.00 struck through, a note that it will reach 42 members, and a Send to 42 Members button',
    caption: 'A final check of the message, price, and audience size together, before it goes out to 42 members',
  },
];

const Flok = () => {
  useNoIndex();
  const IMG = '/images/case-studies/flok';

  return (
    <CaseStudyGate projectName="Flok">
      <div className="min-h-screen bg-primary">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="pt-8">
            <Link
              to="/"
              className="inline-flex items-center text-tertiary hover:text-[#0077A8] transition-colors duration-300"
            >
              ← Back to Home
            </Link>
          </div>

          {/* Header */}
          <header className="pt-10 pb-16 sm:pt-16 sm:pb-20">
            <img src="/images/flok_logo.png" alt="Flok" className="h-12 sm:h-14 object-contain mb-8" />
            <h1 className="text-4xl sm:text-5xl font-bold text-text-primary leading-tight mb-6 max-w-3xl">
              Rearchitecting how small businesses reward loyal customers, from a paper punch card
              into an automated, beacon-powered club.
            </h1>
            <p className="text-tertiary text-sm mb-6"><span className="font-bold">Senior Product Designer</span> · March 2016 - November 2016</p>
            <div className="w-16 h-1 mb-8" style={{ backgroundColor: ACCENT }} />
            <div className="grid grid-cols-[2fr_1fr] gap-6 text-sm max-w-3xl">
              <div>
                <p className="text-tertiary mb-1">Team</p>
                <p className="text-text-primary font-medium">
                  Designed merchant sign-in, onboarding, and analytics. Collaborated with other
                  designers on the campaign and messaging flows, and shipped with a remote
                  development team.
                </p>
              </div>
              <div>
                <p className="text-tertiary mb-1">Timeline</p>
                <p className="text-text-primary font-medium">March 2016 - November 2016</p>
              </div>
              <div>
                <p className="text-tertiary mb-1">Goal</p>
                <p className="text-text-primary font-medium">
                  Redesign onboarding and analytics to feel premium, letting businesses customize
                  their own colors and branding instead of using a generic template.
                </p>
              </div>
              <div>
                <p className="text-tertiary mb-1">Deliverable</p>
                <p className="text-text-primary font-medium">Shipped product</p>
              </div>
            </div>
          </header>

          {/* Context */}
          <section className="py-16 sm:py-24 border-t border-gray-light">
            <SectionHeading eyebrow="Context" title="A punch card that punches itself" />
            <p className="text-tertiary text-lg max-w-3xl">
              Flok paired an in-store beacon with a merchant app to replace the paper punch card.
              When a signed-up customer walked in, the beacon logged the visit automatically, no
              stamp, no scan. Business owners used the app to launch reward campaigns or message
              customers directly, built on who was actually walking through the door.
            </p>
          </section>

          {/* Sign-in and account creation */}
          <section className="py-16 sm:py-24 border-t border-gray-light">
            <SectionHeading
              eyebrow="What I designed"
              title="Sign-in and account creation"
              intro="The merchant-facing entry point: sign in for returning business owners, or a short setup flow for new ones, ending in a live preview of their club before launch."
            />
            <SignInFlow />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-10 mt-16 max-w-2xl mx-auto">
              <PhoneScreen
                src={`${IMG}/splash-signin.gif`}
                alt="Animated Flok business app intro: the blue splash screen gives way to Log In and Sign Up options over illustrated app previews"
              />
              <PhoneScreen
                src={`${IMG}/sign-in.jpg`}
                alt="Flok Sign In screen with email and password fields, a Sign In with Email button, and a Sign In with Facebook button"
              />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-8 mt-16 items-end">
              {ONBOARDING.map(([file, alt]) => (
                <DevicePhone key={file} src={`${IMG}/${file}.png`} alt={alt} />
              ))}
            </div>
          </section>

          {/* Analytics */}
          <section className="py-16 sm:py-24 border-t border-gray-light">
            <SectionHeading
              eyebrow="What I designed"
              title="Analytics"
              intro="Business owners' club at a glance: total members, sign-up sources, ratings, and check-ins over time."
            />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-10">
              {ANALYTICS.map(({ file, alt, label, caption }) => (
                <figure key={file}>
                  <DevicePhone src={`${IMG}/${file}.png`} alt={alt} />
                  <figcaption className="mt-4 text-sm text-tertiary max-w-[240px] mx-auto">
                    <span className="font-bold mr-1.5" style={{ color: ACCENT_TEXT }}>
                      {label}
                    </span>
                    {caption}
                  </figcaption>
                </figure>
              ))}
            </div>
            <p className="text-tertiary mt-12 max-w-3xl">
              Analytics also tracked how reward and message campaigns performed, though no screen
              for it survived in the original files.
            </p>
          </section>

          {/* Built alongside it */}
          <section className="py-16 sm:py-24 border-t border-gray-light">
            <SectionHeading
              eyebrow="Co-designed with the team"
              title="Built alongside it"
              intro="Reward campaigns and direct customer messaging, designed with other designers and built with a remote development team."
            />
            <CampaignFlow />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-14 mt-16">
              {CAMPAIGN_SCREENS.map(({ file, alt, caption }) => (
                <figure key={file}>
                  <PhoneScreen src={`${IMG}/${file}`} alt={alt} />
                  <figcaption className="mt-4 text-sm text-tertiary max-w-[260px] mx-auto">{caption}</figcaption>
                </figure>
              ))}
            </div>
          </section>

          {/* Where Flok stands now */}
          <section className="py-16 sm:py-24 border-t border-gray-light">
            <h2 className="text-2xl sm:text-3xl font-bold text-text-primary mb-6 max-w-2xl">
              Where Flok stands now
            </h2>
            <p className="text-tertiary text-lg max-w-3xl">
              Flok was acquired by Wix, shortly after I moved on to my next role.
            </p>
          </section>

          <OtherProjects current="flok" />
        </div>
      </div>
      <Footer />
    </CaseStudyGate>
  );
};

export default Flok;
